import React, { useState } from "react";
import Toggle from "../../components/ui/Toggle";
import { useTranslation } from "react-i18next";
import RepresentativeDocumentsTab from "./tabs/RepresentativeDocumentsTab";
import RepresentativeProfileForm from "../../pages/Representatives/RepresentativeProfileForm";

export default function RepresentativeEditModal({
  rep,
  onClose,
  onSave,
  allRepresentatives = [],
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("profile");
  const [form, setForm] = useState({
    firstName: rep.firstName || "",
    lastName: rep.lastName || "",
    phoneNumber: rep.phoneNumber || "",
    email: rep.email || "",
    city: rep.city || "",
    isActive: rep.isActive || false,
    parentId: rep.parentId || null,
  });
  console.log(rep);

  const getDescendantIds = (representatives, currentId) => {
    const descendants = new Set();

    const recurse = (id) => {
      representatives.forEach((r) => {
        if (r.parentId === id) {
          descendants.add(r.id);
          recurse(r.id);
        }
      });
    };

    recurse(currentId);
    return descendants;
  };

  const descendantIds = rep.id
    ? getDescendantIds(allRepresentatives, rep.id)
    : new Set();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError("");
    setLoading(true);

    // Sécurise le payload: on n’envoie que ce que l’API attend
    const payload = {
      firstName: form.firstName?.trim(),
      lastName: form.lastName?.trim(),
      phoneNumber: form.phoneNumber?.trim(),
      email: form.email?.trim(),
      location: form.location?.trim(),
      isActive: !!form.isActive,
      parentId: form.parentId || null,
      // optionnels si supportés sur ce PUT:
      displayName: form.displayName?.trim() || undefined,
      availability: form.availability?.trim() || undefined,
      code: form.code?.trim() || undefined,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/profile/${rep.userId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Erreur lors de la mise à jour");
      }

      onSave?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const handleParentChange = async (e) => {
    const newParentId = e.target.value || null;

    // Optimistic UI: on met à jour l’état tout de suite
    setForm((prev) => ({ ...prev, parentId: newParentId }));

    try {
      // Body = GUID en JSON primitif (ex: "xxxxxxxx-...."), pas { parentId: ... }
      const body = newParentId ?? "00000000-0000-0000-0000-000000000000"; // Guid.Empty = retirer le parrain

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/${rep.id}/assign-parent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Erreur lors du changement de parrain");
      }

      onSave?.();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center overflow-auto p-4 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full p-6 space-y-4 relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-2">
          {t("Modifier le représentant")}
        </h2>

        {/* Onglets */}
        <div className="tabs mb-4">
          <button
            className={`tab tab-bordered ${
              activeTab === "profile" && "tab-active"
            }`}
            onClick={() => setActiveTab("profile")}
          >
            {t("Profil")}
          </button>
          <button
            className={`tab tab-bordered ${
              activeTab === "clients" && "tab-active"
            }`}
            onClick={() => setActiveTab("clients")}
          >
            {t("Clients")}
          </button>
          <button
            className={`tab tab-bordered ${
              activeTab === "subscriptions" && "tab-active"
            }`}
            onClick={() => setActiveTab("subscriptions")}
          >
            {t("Abonnements")}
          </button>
          <button
            className={`tab tab-bordered ${
              activeTab === "subscriptions" && "tab-active"
            }`}
            onClick={() => setActiveTab("parrainage")}
          >
            {t("Parrainage")}
          </button>
          <button
            className={`tab tab-bordered ${
              activeTab === "documents" && "tab-active"
            }`}
            onClick={() => setActiveTab("documents")}
          >
            {t("Documents")}
          </button>
        </div>

        {/* Contenu des tabs */}
        {activeTab === "profile" && (
          <>
            <RepresentativeProfileForm
              userId={rep.userId} // ← clé: on édite le profil du représentant
              onSaved={onSave} // ← rafraîchir la liste parent si besoin
              showIntro={false}
              showTitle={false}
              compact
              saveButtonLabel={t("global.save")}
            />
          </>
        )}

        {activeTab === "clients" && (
          <div className="text-gray-600">
            <p>
              La liste des clients du représentant sera affichée ici. (à venir)
            </p>
          </div>
        )}

        {activeTab === "subscriptions" && (
          <div className="text-gray-600">
            <p>
              Les abonnements vendus par ce représentant apparaîtront ici. (à
              venir)
            </p>
          </div>
        )}

        {activeTab === "parrainage" && (
          <div className="text-gray-600">
            <p className="mb-2 font-medium">
              {form.parentId
                ? `Parrain actuel : ${
                    allRepresentatives.find((r) => r.id === form.parentId)
                      ?.firstName ?? ""
                  } ${
                    allRepresentatives.find((r) => r.id === form.parentId)
                      ?.lastName ?? ""
                  }`
                : "Aucun parrainage pour ce représentant."}
            </p>
            <div className="mt-4">
              <label className="block mb-1 font-semibold">
                Parrain (optionnel)
              </label>
              <select
                name="parentId"
                value={form.parentId || ""}
                onChange={handleParentChange}
                className="select select-bordered w-full"
              >
                <option value="">Aucun parrain</option>
                {allRepresentatives
                  .filter((r) => r.id !== rep.id && !descendantIds.has(r.id))
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.firstName} {r.lastName} ({r.code})
                    </option>
                  ))}
              </select>
            </div>
          </div>
        )}

        {activeTab === "documents" && (
          <RepresentativeDocumentsTab repId={rep.id} repUserId={rep.userId} />
        )}
      </div>
    </div>
  );
}
